///
/// EarthBrowser
///
/// Created by Matt Giger on 4/14/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { CoordFooter, InfoField, MoreSection, SectionTitle } from "@/components/controls/anchored-panel/panelAtoms"
import {
	cMinPassElevationDeg,
	compassDirection,
	lookAnglesAt,
	predictNextPass,
	satrecFrom,
	type ObserverLocation,
	type PassPrediction
} from "@/earthbrowser/dataset-distribution/satellites/satelliteOrbit"
import { formatSatelliteAltitude } from "@/earthbrowser/utils/unitFormatters"
import type { SatelliteEntry } from "@shared-types/satellite-entry"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { SatRec } from "satellite.js"

export function AnchoredSatelliteInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "satellite" ? data : null}
			fallbackTitle={content => content.satellite.object_name}
			accentBorder="border-blue-400/20"
			pill="bg-blue-400/90"
		>
			{(content, ctx) => (
				<SatelliteBody
					satellite={content.satellite}
					livePosition={content.livePosition}
					liveSpeed={content.liveSpeed}
					ctx={ctx}
				/>
			)}
		</AnchoredPanelShell>
	)
}

interface SatelliteBodyProps {
	satellite: SatelliteEntry
	livePosition?: { lat: number; lon: number; alt: number }
	liveSpeed?: number
	ctx: PanelShellContext
}

function SatelliteBody({ satellite, livePosition, liveSpeed, ctx }: SatelliteBodyProps) {
	const periodMin = 1440 / satellite.mean_motion
	const orbitType = classifyOrbit(livePosition?.alt ?? periodMin * 7.9 - 95)
	const satrec = useMemo(() => satrecFrom(satellite), [satellite])
	const [showElements, setShowElements] = useState(false)

	return (
		<>
			<div className="text-[22px] leading-none font-semibold text-white truncate">
				{ctx.title}
			</div>

			<div className="flex items-center gap-2 text-sm text-slate-300">
				<span className="text-blue-300">NORAD {satellite.norad_cat_id}</span>
				<span className="text-slate-500">&bull;</span>
				<span className="text-slate-400">{orbitType}</span>
			</div>

			<NextPassSection satrec={satrec} satelliteName={ctx.title} />

			<MoreSection compact={ctx.compact}>
				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
						{livePosition && (
							<>
								<InfoField label="Lat" value={formatLatitude(livePosition.lat)} />
								<InfoField label="Lon" value={formatLongitude(livePosition.lon)} />
								<InfoField label="Altitude" value={formatSatelliteAltitude(livePosition.alt)} />
							</>
						)}
						{liveSpeed != null && (
							<InfoField label="Speed" value={`${(liveSpeed).toFixed(2)} km/s`} />
						)}
						<InfoField label="Period" value={`${periodMin.toFixed(1)} min`} />
						<InfoField label="Inclination" value={`${satellite.inclination.toFixed(1)}°`} />
					</div>

					{livePosition && (
						<CoordFooter
							lat={livePosition.lat}
							lon={livePosition.lon}
							extra={formatSatelliteAltitude(livePosition.alt)}
							className="pt-1 border-t border-white/5"
						/>
					)}
				</div>

				<div className="space-y-1.5">
					<button
						onClick={() => setShowElements(v => !v)}
						className="pointer-events-auto flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-400 hover:text-blue-300 transition-colors"
					>
						<svg
							width="8" height="8" viewBox="0 0 8 8" fill="currentColor"
							className={`transition-transform duration-200 ${showElements ? "rotate-90" : ""}`}
						>
							<path d="M2 0 L7 4 L2 8 Z" />
						</svg>
						Orbital Elements
					</button>
					{showElements && (
						<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
							<InfoField label="Eccentricity" value={satellite.eccentricity < 0.001 ? satellite.eccentricity.toExponential(2) : satellite.eccentricity.toFixed(4)} />
							<InfoField label="Rev/Day" value={satellite.mean_motion.toFixed(2)} />
							<InfoField label="RAAN" value={`${satellite.ra_of_asc_node.toFixed(2)}°`} />
							<InfoField label="Arg Perigee" value={`${satellite.arg_of_pericenter.toFixed(2)}°`} />
							<InfoField label="Mean Anomaly" value={`${satellite.mean_anomaly.toFixed(2)}°`} />
							<InfoField label="B*" value={satellite.bstar.toExponential(3)} />
						</div>
					)}
				</div>
			</MoreSection>
		</>
	)
}

// ========== Next Pass ==========

interface NextPassSectionProps {
	satrec: SatRec
	satelliteName: string
}

function NextPassSection({ satrec, satelliteName }: NextPassSectionProps) {
	const { location, status, request } = useObserverLocation()
	const [prediction, setPrediction] = useState<PassPrediction | null>(null)

	useEffect(() => {
		if (!location) {
			setPrediction(null)
			return
		}
		setPrediction(predictNextPass(satrec, location))

		// Recompute once the current pass has ended (or none was found)
		const interval = setInterval(() => {
			setPrediction(prev => {
				if (prev?.kind === "geostationary") return prev
				if (prev?.kind === "pass" && Date.now() < prev.set.getTime()) return prev
				return predictNextPass(satrec, location)
			})
		}, 30_000)
		return () => clearInterval(interval)
	}, [satrec, location])

	return (
		<div className="rounded-lg border border-blue-400/25 bg-blue-500/[0.08] px-3 py-2.5 space-y-1">
			<SectionTitle accentClass="text-blue-400">Next Pass</SectionTitle>
			<NextPassBody
				satrec={satrec}
				satelliteName={satelliteName}
				location={location}
				status={status}
				request={request}
				prediction={prediction}
			/>
		</div>
	)
}

interface NextPassBodyProps {
	satrec: SatRec
	satelliteName: string
	location: ObserverLocation | null
	status: "idle" | "locating" | "error"
	request: () => void
	prediction: PassPrediction | null
}

function NextPassBody({ satrec, satelliteName, location, status, request, prediction }: NextPassBodyProps) {
	if (!location) {
		return (
			<div className="space-y-2 pt-0.5">
				<div className="text-xs text-slate-300">
					See when {satelliteName} passes over your location
				</div>
				<button
					onClick={request}
					disabled={status === "locating"}
					className="pointer-events-auto w-full rounded-md bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-200 text-xs font-medium py-1.5 transition-colors disabled:opacity-50"
				>
					{status === "locating" ? "Locating…" : "Use my location"}
				</button>
				{status === "error" && (
					<div className="text-[11px] text-red-400">Couldn&apos;t get your location</div>
				)}
			</div>
		)
	}

	if (!prediction) {
		return <div className="text-xs text-slate-400">Calculating…</div>
	}

	if (prediction.kind === "geostationary") {
		return prediction.visible ? (
			<>
				<div className="text-lg font-semibold text-white leading-tight">In view now</div>
				<div className="text-xs text-slate-300">
					Geostationary — look {compassDirection(prediction.azimuthDeg)}, {Math.round(prediction.elevationDeg)}° above the horizon
				</div>
			</>
		) : (
			<>
				<div className="text-lg font-semibold text-white leading-tight">Not visible</div>
				<div className="text-xs text-slate-300">
					Geostationary — always below the horizon from your location
				</div>
			</>
		)
	}

	if (prediction.kind === "none") {
		return (
			<>
				<div className="text-lg font-semibold text-white leading-tight">No passes soon</div>
				<div className="text-xs text-slate-300">
					None above {cMinPassElevationDeg}° from your location in the next {prediction.searchHours} hours
				</div>
			</>
		)
	}

	const now = Date.now()
	if (now >= prediction.set.getTime()) {
		return <div className="text-xs text-slate-400">Calculating…</div>
	}

	if (now >= prediction.rise.getTime()) {
		const live = lookAnglesAt(satrec, location, new Date())
		return (
			<>
				<div className="text-lg font-semibold text-white leading-tight">Passing over now</div>
				<div className="text-xs text-slate-300">
					{live ? `Look ${compassDirection(live.azimuthDeg)} · ${Math.round(live.elevationDeg)}° up · ` : ""}
					sets at {formatClockTime(prediction.set)}
				</div>
			</>
		)
	}

	const durationMin = Math.max(1, Math.round((prediction.set.getTime() - prediction.rise.getTime()) / 60000))
	return (
		<>
			<div className="text-lg font-semibold text-white leading-tight">
				{formatCountdown(prediction.rise.getTime() - now)}
			</div>
			<div className="text-xs text-slate-300">
				Rises {compassDirection(prediction.riseAzimuthDeg)} at {formatClockTime(prediction.rise)} · peaks {Math.round(prediction.maxElevationDeg)}° · {durationMin} min
			</div>
		</>
	)
}

// ========== Observer Location ==========

const cLocationStorageKey = "eb-observer-location"

function readStoredLocation(): ObserverLocation | null {
	try {
		const raw = localStorage.getItem(cLocationStorageKey)
		if (!raw) return null
		const parsed = JSON.parse(raw) as { lat?: unknown, lon?: unknown }
		if (typeof parsed.lat === "number" && typeof parsed.lon === "number") {
			return { lat: parsed.lat, lon: parsed.lon }
		}
	} catch {
		// ignore malformed storage
	}
	return null
}

function useObserverLocation() {
	const [location, setLocation] = useState<ObserverLocation | null>(readStoredLocation)
	const [status, setStatus] = useState<"idle" | "locating" | "error">("idle")

	const request = useCallback(() => {
		if (!navigator.geolocation) {
			setStatus("error")
			return
		}
		setStatus("locating")
		navigator.geolocation.getCurrentPosition(
			position => {
				const loc = { lat: position.coords.latitude, lon: position.coords.longitude }
				try {
					localStorage.setItem(cLocationStorageKey, JSON.stringify(loc))
				} catch {
					// storage unavailable, keep in-memory only
				}
				setLocation(loc)
				setStatus("idle")
			},
			() => setStatus("error"),
			{ enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 }
		)
	}, [])

	// If permission was already granted, fetch the location without prompting
	useEffect(() => {
		if (location) return
		navigator.permissions?.query({ name: "geolocation" })
			.then(result => {
				if (result.state === "granted") request()
			})
			.catch(() => {
				// permissions API unavailable, wait for the user to ask
			})
	}, [location, request])

	return { location, status, request }
}

// ========== Formatting ==========

function formatCountdown(ms: number): string {
	const totalMin = Math.floor(ms / 60000)
	if (totalMin < 1) return "In under a minute"
	if (totalMin < 60) return `In ${totalMin} min`
	const hours = Math.floor(totalMin / 60)
	if (hours < 24) {
		const min = totalMin % 60
		return min > 0 ? `In ${hours} h ${min} min` : `In ${hours} h`
	}
	const days = Math.floor(hours / 24)
	const remHours = hours % 24
	return remHours > 0 ? `In ${days} d ${remHours} h` : `In ${days} d`
}

function formatClockTime(date: Date): string {
	return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

function formatLatitude(lat: number): string {
	return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? "N" : "S"}`
}

function formatLongitude(lon: number): string {
	return `${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? "E" : "W"}`
}

function classifyOrbit(altKm: number): string {
	if (altKm < 2000) return "LEO"
	if (altKm < 35786 - 500) return "MEO"
	if (altKm < 35786 + 500) return "GEO"
	return "HEO"
}
